/** Normalisation, validation and formatting helpers for customer records. */

/** Strip everything but digits — canonical phone match key (CM-BO-2.2). */
export function normalizePhone(phone: string | undefined | null): string {
  return (phone ?? '').replace(/\D/g, '');
}

/** Lowercased, trimmed email — canonical email match key. */
export function normalizeEmail(email: string | undefined | null): string {
  return (email ?? '').trim().toLowerCase();
}

/** Basic email format check. */
export function isValidEmail(email: string): boolean {
  const v = email.trim();
  if (!v) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/** A name is valid only if it has at least one alphanumeric character. */
export function isMeaningfulName(name: string): boolean {
  return /[\p{L}\p{N}]/u.test(name ?? '');
}

/**
 * Present a digits-only phone in a friendly canonical format.
 * US-style for 10/11 digit numbers, otherwise return the trimmed input.
 */
export function formatPhone(phone: string | undefined | null): string {
  const d = normalizePhone(phone);
  if (d.length === 11 && d.startsWith('1')) {
    return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return (phone ?? '').trim();
}

/** Format a currency amount as USD. */
export function formatMoney(amount: number | undefined | null): string {
  const n = Number(amount ?? 0) || 0;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** e.g. "17 Jun 2026". Returns '—' for empty values. */
export function formatDate(iso: string | undefined | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** e.g. "17 Jun 2026, 14:30". */
export function formatDateTime(iso: string | undefined | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Relative-ish last-visit label (Today / Yesterday / N days ago / date). */
export function formatLastVisit(iso: string | undefined | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Never';
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return formatDate(iso);
}

/** Generate a reasonably unique id for new entities / audit entries. */
export function genId(prefix = 'cus'): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export type LastVisitWindow = 'any' | '7d' | '30d' | '90d' | 'never';

/** Does the customer's last visit fall inside the selected window? */
export function matchesLastVisitWindow(lastVisitAt: string | undefined, window: LastVisitWindow): boolean {
  if (window === 'any') return true;
  if (window === 'never') return !lastVisitAt;
  if (!lastVisitAt) return false;
  const d = new Date(lastVisitAt);
  if (Number.isNaN(d.getTime())) return false;
  const days = (Date.now() - d.getTime()) / 86_400_000;
  if (window === '7d') return days <= 7;
  if (window === '30d') return days <= 30;
  if (window === '90d') return days <= 90;
  return true;
}
